import { useEffect, useState } from 'react';

// ----------------------------------------------------------------------

export default function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue === null ? defaultValue : JSON.parse(storedValue);
    } catch (error) {
      console.warn(`Error parsing localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    const listener = (e: StorageEvent): void => {
      if (e.storageArea === localStorage && e.key === key) {
        try {
          setValue(e.newValue ? JSON.parse(e.newValue) : defaultValue);
        } catch (error) {
          console.warn(`Error parsing storage event for key "${key}":`, error);
          setValue(defaultValue);
        }
      }
    };
    window.addEventListener('storage', listener);

    return (): void => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: T | ((prev: T) => T)): void => {
    setValue((currentValue) => {
      const result =
        typeof newValue === 'function'
          ? (newValue as (prev: T) => T)(currentValue)
          : newValue;
      try {
        localStorage.setItem(key, JSON.stringify(result));
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
      return result;
    });
  };

  return [value, setValueInLocalStorage];
}
